import { fetchJSON as fetchAPI } from './client';

export type InventoryDashboard = {
  tenant_id: string;
  total_skus: number;
  total_units: number;
  avg_days_of_cover: number;
  avg_health_score: number;
  stockout_risk_count: number;
  overstock_risk_count: number;
  aging_units: number;
  active_alerts: number;
  pending_replenishments: number;
  active_recommendations: number;
  forecast_accuracy_pct: number;
  generated_at: string;
};

export type InventoryHealthRecord = {
  id: string;
  sku: string;
  asin?: string;
  domain: string;
  on_hand: number;
  inbound: number;
  reserved: number;
  days_of_cover: number;
  turnover_rate: number;
  stockout_risk: string;
  overstock_risk: string;
  aging_days: number;
  health_score: number;
  classification?: string;
};

export type DemandForecast = {
  id: string;
  sku: string;
  asin?: string;
  horizon: string;
  units: number;
  growth_pct: number;
  confidence: number;
  factors?: string[];
};

export type ReplenishmentPlan = {
  id: string;
  sku: string;
  asin?: string;
  purchase_qty: number;
  production_qty?: number;
  reorder_date: string;
  shipment_date?: string;
  fba_dispatch_date?: string;
  safety_stock: number;
  buffer_stock: number;
  urgency?: string;
};

export type SupplierRecord = {
  id: string;
  name: string;
  lead_time_days: number;
  moq: number;
  reliability_score: number;
  capacity_units?: number;
  active: boolean;
};

export type InventoryRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  sku?: string;
  quantity?: number;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

export type ScenarioRecord = {
  id: string;
  name: string;
  type: string;
  outcome?: string;
  stockout_risk?: number;
  overstock_risk?: number;
  days_of_cover?: number;
  created_at: string;
};

export type InventoryAlert = {
  id: string;
  title: string;
  description?: string;
  severity: string;
  sku?: string;
  acknowledged: boolean;
  created_at: string;
};

export type RiskSummary = {
  tenant_id: string;
  skus: number;
  stockout_risk: Record<string, number>;
  overstock_risk: Record<string, number>;
};

const API_BASE = import.meta.env.VITE_INVENTORYINTEL_API ?? '/api/v1/inventoryintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const inventoryintelApi = {
  dashboard: () => fetchJSON<InventoryDashboard>('/dashboard'),
  listHealth: () => fetchJSON<InventoryHealthRecord[]>('/health'),
  listForecasts: (horizon?: string) =>
    fetchJSON<DemandForecast[]>(horizon ? `/forecasts?horizon=${horizon}` : '/forecasts'),
  listReplenishment: () => fetchJSON<ReplenishmentPlan[]>('/replenishment'),
  listSuppliers: () => fetchJSON<SupplierRecord[]>('/suppliers'),
  listRecommendations: () => fetchJSON<InventoryRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<InventoryRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<InventoryRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listScenarios: () => fetchJSON<ScenarioRecord[]>('/scenarios'),
  runScenario: (body: { name: string; type: string; parameters?: Record<string, unknown> }) =>
    fetchJSON<ScenarioRecord>('/scenarios', { method: 'POST', body: JSON.stringify(body) }),
  listAlerts: () => fetchJSON<InventoryAlert[]>('/alerts'),
  acknowledgeAlert: (id: string) =>
    fetchJSON<InventoryAlert>(`/alerts/${id}/acknowledge`, { method: 'POST' }),
  riskSummary: () => fetchJSON<RiskSummary>('/risk'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
